import { REACT_APP_ENV } from '../../../../../../../environments/environment';

// Generated by Copilot

const NFC_PASSWORD_LENGTH = 12;
const NFC_SECRET_BYTES = 32;
const DEFAULT_NFC_PAYLOAD_VERSION = 'v1';
const PASSWORD_CHARS =
    'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';

/**
 * Generates cryptographically secure random bytes.
 *
 * @param length Number of bytes to generate.
 * @returns Uint8Array filled with cryptographically secure random bytes.
 * @throws Error When the Web Crypto API is not available (for example, in a non-HTTPS or otherwise insecure context).
 * @author Copilot
 */
const getSecureRandomBytes = (length: number) => {
    if (typeof globalThis.crypto?.getRandomValues !== 'function') {
        throw new Error(
            'Secure random number generator is not available. ' +
                'Ensure this application is served over HTTPS in a secure context so that crypto.getRandomValues is accessible.'
        );
    }
    const bytes = new Uint8Array(length);
    globalThis.crypto.getRandomValues(bytes);

    return bytes;
};

const toHex = (bytes: Uint8Array) =>
    Array.from(bytes)
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join('');

const generatePassword = (length: number) => {
    const randomBytes = getSecureRandomBytes(length);

    return Array.from(randomBytes)
        .map(byte => PASSWORD_CHARS[byte % PASSWORD_CHARS.length])
        .join('');
};

export const generateLockerNfcCredentials = () => {
    const nfcSecret = toHex(getSecureRandomBytes(NFC_SECRET_BYTES));
    const nfcPassword = generatePassword(NFC_PASSWORD_LENGTH);

    return {
        nfcSecret,
        nfcPassword,
    };
};

export interface LockerNfcUrl {
    cubbyId: number;
    url: string;
}

const APP_SCHEME_MAP: Record<typeof REACT_APP_ENV, string> = {
    development: 'barboxx-development',
    dev: 'barboxx-dev',
    production: 'barboxx',
};

const APP_SCHEME = APP_SCHEME_MAP[REACT_APP_ENV];

const buildNfcSignatureMessage = ({
    businessId,
    lockerId,
    cubbyId,
    version,
}: {
    businessId: number;
    lockerId: number;
    cubbyId: number;
    version: string;
}) => `${version}:${businessId}:${lockerId}:${cubbyId}`;

const signHmacSha256Hex = async ({
    secret,
    message,
}: {
    secret: string;
    message: string;
}) => {
    if (!globalThis.crypto?.subtle) {
        throw new Error(
            'This browser cannot generate NFC signatures. Use a secure browser context (HTTPS/localhost).'
        );
    }

    const encoder = new TextEncoder();
    const key = await globalThis.crypto.subtle.importKey(
        'raw',
        encoder.encode(secret),
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
    );
    const signatureBuffer = await globalThis.crypto.subtle.sign(
        'HMAC',
        key,
        encoder.encode(message)
    );

    return toHex(new Uint8Array(signatureBuffer));
};

export const generateLockerNfcPickupUrls = async ({
    businessId,
    lockerId,
    numberOfCubbies,
    nfcSecret,
    version = DEFAULT_NFC_PAYLOAD_VERSION,
}: {
    businessId: number;
    lockerId: number;
    numberOfCubbies: number;
    nfcSecret?: string;
    version?: string;
}): Promise<LockerNfcUrl[]> => {
    if (!nfcSecret) {
        throw new Error('Locker NFC secret is required to generate tag URLs.');
    }

    const cubbyIds = Array.from(
        { length: Math.max(0, numberOfCubbies) },
        (_, index) => index
    );

    const generatedUrls = await Promise.all(
        cubbyIds.map(async cubbyId => {
            const message = buildNfcSignatureMessage({
                businessId,
                lockerId,
                cubbyId,
                version,
            });
            const signature = await signHmacSha256Hex({
                secret: nfcSecret,
                message,
            });
            const params = `bid=${businessId}&lid=${lockerId}&cid=${cubbyId}&v=${version}&sig=${signature}`;

            return {
                cubbyId,
                url: `${APP_SCHEME}://pickup?${params}`,
            };
        })
    );

    return generatedUrls;
};
